import axios from "axios";
import config from "../config/config";

const API_URL = config.API_URL;

// Configure axios
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle auth-related errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response &&
      (error.response.status === 401 || error.response.status === 403)
    ) {
      // Don't redirect if it's a candidate login/verify OTP request
      const isCandidateAuthRequest = 
        error.config?.url?.includes('/auth/candidate/login') ||
        error.config?.url?.includes('/auth/candidate/verify');
      
      if (!isCandidateAuthRequest) {
        localStorage.removeItem("token");
        localStorage.removeItem("role");
        
        // Redirect based on current location
        const currentPath = window.location.pathname;
        if (currentPath.startsWith('/candidate')) {
          window.location.href = "/candidate-login";
        } else {
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (email, password) =>
    axios.post(`${API_URL}/auth/login`, { email, password }),
  resetFirstTimePassword: (userId, newPassword) =>
    axios.put(`${config.API_URL}/auth/reset-password/${userId}`, { newPassword }),
  candidateLogin: (email) =>
    axios.post(`${API_URL}/auth/candidate/login`, { email }),
  verifyOtp: (email, otp) =>
    axios.post(`${API_URL}/auth/candidate/verify`, { email, otp }),
  getProfile: () => axios.get(`${API_URL}/auth/profile`),
  updateProfile: (data) => axios.put(`${API_URL}/auth/profile`, data),
  verify2fa: (email, code) =>
    axios.post(`${API_URL}/auth/verify-2fa`, { email, code }),
  setup2fa: () => axios.post(`${API_URL}/auth/setup-2fa`),
  enable2fa: (token) => axios.post(`${API_URL}/auth/enable-2fa`, { token }),
  disable2fa: (token, password) =>
    axios.post(`${API_URL}/auth/disable-2fa`, { token, password }),
  generateBackupCodes: (token, password) =>
    axios.post(`${API_URL}/auth/generate-backup-codes`, { token, password }),
  sessionSettings: (settings) =>
    axios.post(`${API_URL}/auth/session-settings`, settings),
  registerCompany: (companyData) =>
    axios.post(`${API_URL}/company/register`, companyData),
};

export const adminAPI = {
  // Company Super Admin APIs for managing company admins
  getAllCompanyAdmins: () => axios.get(`${API_URL}/admin/company-admins`),
  getCompanyAdmin: (id) => axios.get(`${API_URL}/admin/company-admins/${id}`),
  createCompanyAdmin: (adminData) =>
    axios.post(`${API_URL}/admin/company-admins`, adminData),
  updateCompanyAdmin: (id, adminData) =>
    axios.put(`${API_URL}/admin/company-admins/${id}`, adminData),
  deleteCompanyAdmin: (id) =>
    axios.delete(`${API_URL}/admin/company-admins/${id}`),
  getCompanyAdminStats: () =>
    axios.get(`${API_URL}/admin/company-admins/stats`),
};

export const candidateFoldersAPI = {
  getAll: (params) => axios.get(`${API_URL}/candidate-folders`, { params }),
  getOne: (id) => axios.get(`${API_URL}/candidate-folders/${id}`),
  create: (folderData) => axios.post(`${API_URL}/candidate-folders`, folderData),
  update: (id, folderData) => axios.put(`${API_URL}/candidate-folders/${id}`, folderData),
  delete: (id, confirmation) => axios.delete(`${API_URL}/candidate-folders/${id}`, {
    data: { confirmation }
  })
};

export const candidatesAPI2 = {

  getMyFolderCandidates: ({ folderId, page, limit, search, isMyCandidate }) => {
    return axios.post(`${API_URL}/candidates2/folder-candidates`, {
      folderId,
      page,
      limit,
      search,
      isMyCandidate
    });
  },
  getMy: ({ page = 1, limit = 5, candidatesPage = 1, candidatesLimit = 5 }) =>
    axios.post(`${API_URL}/candidates2/my`, {
      page,
      limit,
      candidatesPage,
      candidatesLimit,
    }),

  getCompany: ({ page = 1, limit = 5, candidatesPage = 1, candidatesLimit = 5 }) =>
    axios.post(`${API_URL}/candidates2/company`, {
      page,
      limit,
      candidatesPage,
      candidatesLimit,
    }),

  getAll: (params) => axios.get(`${API_URL}/candidates2`, {
    params: {
      ...params,
      groupByFolder: params.viewMode === 'folder'
    }
  }),

  getOne: (id) => axios.get(`${API_URL}/candidates2/${id}`),
  create: (candidateData) => axios.post(`${API_URL}/candidates2`, candidateData),
  update: (id, candidateData) => axios.put(`${API_URL}/candidates2/${id}`, candidateData),
  delete: (id) => axios.delete(`${API_URL}/candidates2/${id}`),

  getFolderCandidates: (folderId, { page = 1, limit = 10, search = '' }) =>
    axios.get(`${API_URL}/candidates2/folder/${folderId}`, {
      params: {
        page,
        limit,
        search
      }
    }),
};

export const examAssignmentsAPI = {
  getAll: (params) => axios.get(`${API_URL}/exam-assignments`, { params }),
  getMyAssignments: (params) => axios.get(`${API_URL}/exam-assignments/my`, { params }),

  getCompanyAssignments: (params) => axios.get(`${API_URL}/exam-assignments/company`, { params }),

  getOne: (id) => axios.get(`${API_URL}/exam-assignments/${id}`),
  create: (assignmentData) =>
    axios.post(`${API_URL}/exam-assignments`, assignmentData),
  update: (id, assignmentData) =>
    axios.put(`${API_URL}/exam-assignments/${id}`, assignmentData),
  delete: (id) => axios.delete(`${API_URL}/exam-assignments/${id}`),
  resendInvitation: (id) =>
    axios.post(`${API_URL}/exam-assignments/${id}/resend-invitation`),
  getAvailableCandidates: (params) =>
    axios.get(`${API_URL}/exam-assignments/available-candidates`, { params }),
};

export const candidatesAPI = {
  getAll: (params) => axios.get(`${API_URL}/candidates`, { params }),
  getOne: (id) => axios.get(`${API_URL}/candidates/${id}`),
  create: (candidateData) => axios.post(`${API_URL}/candidates`, candidateData),
  update: (id, candidateData) =>
    axios.put(`${API_URL}/candidates/${id}`, candidateData),
  delete: (id) => axios.delete(`${API_URL}/candidates/${id}`),
  resendInvitation: (id) =>
    axios.post(`${API_URL}/candidates/resend-invitation/${id}`),
  import: (fileData) => {
    const formData = new FormData();
    formData.append("file", fileData);
    return axios.post(`${API_URL}/candidates/import`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  assignExam: (candidateId, examData) =>
    axios.post(`${API_URL}/candidates/${candidateId}/assign-exam`, examData),
  getResults: (id) => axios.get(`${API_URL}/candidates/${id}/results`),
  resumeExam: (candidateId) =>
    axios.post(`${API_URL}/candidates/${candidateId}/resume-exam`),
  checkExamEligibility: (candidateId) =>
    axios.get(`${API_URL}/candidates/${candidateId}/exam-eligibility`),
};

export const questionsAPI = {
  getAll: (params) => axios.get(`${API_URL}/questions`, { params }),
  getById: (id) => axios.get(`${API_URL}/questions/${id}`),
  create: (data) => axios.post(`${API_URL}/questions`, data),
  update: (id, data) => axios.put(`${API_URL}/questions/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/questions/${id}`),
  bulkCreate: (questions) =>
    axios.post(`${API_URL}/questions/bulk-create`, { questions }),
  bulkUpdate: (questions) =>
    axios.put(`${API_URL}/questions/bulk-update`, { questions }),
  bulkDelete: (questionIds) =>
    axios.delete(`${API_URL}/questions/bulk-delete`, { data: { questionIds } }),
  downloadSelected: (questionIds) =>
    axios
      .post(
        `${API_URL}/questions/download-selected`,
        { questionIds },
        { responseType: "blob" }
      )
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "selected-questions.xlsx");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }),
  downloadAll: (questionBankId) =>
    axios
      .get(`${API_URL}/questions/download-all/${questionBankId}`, {
        responseType: "blob",
      })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "all-questions.xlsx");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }),
  uploadMedia: (data) => axios.post(`${API_URL}/questions/upload-media`, data),
  getStats: () => axios.get(`${API_URL}/questions/stats`),
  getByCategory: (category, params) =>
    axios.get(`${API_URL}/questions/by-category/${category}`, { params }),
  getCategories: () => axios.get(`${API_URL}/questions/categories/all`),
  getTypes: () => axios.get(`${API_URL}/questions/types/all`),
  bulkImport: (data) => axios.post(`${API_URL}/questions/bulk-import`, data),
  submitValidatedQuestions: async (data) => {
    const response = await axios.post(
      `${API_URL}/question-bank/submitValidatedQuestions`,
      data
    );
    return response.data;
  },
};
export const examsAPI = {
  getAll: (params) => axios.get(`${API_URL}/exams`, { params }),
  getOne: (id) => axios.get(`${API_URL}/exams/${id}`),
  create: (examData) => axios.post(`${API_URL}/exams`, examData),
  canEdit: async (examId) => {
    try {
      const response = await axios.get(`${API_URL}/exams/can-edit/${examId}`);
      return response;
    } catch (error) {
      console.error("Can Edit Exam API Error:", error);
      throw error;
    }
  },
  update: (id, examData) => axios.put(`${API_URL}/exams/${id}`, examData),
  delete: (id) => axios.delete(`${API_URL}/exams/${id}`),
  getSession: (sessionId) =>
    axios.get(`${API_URL}/exams/sessions/${sessionId}`),
  saveAnswer: (sessionId, data) =>
    axios.post(`${API_URL}/exams/sessions/${sessionId}/save-answer`, data),
  updateProgress: (sessionId, data) =>
    axios.post(`${API_URL}/exams/sessions/${sessionId}/update-progress`, data),
  completeExam: (sessionId) =>
    axios.post(`${API_URL}/exams/sessions/${sessionId}/complete`),
  disqualify: (sessionId, reason) =>
    axios.post(`${API_URL}/exams/sessions/${sessionId}/disqualify`, { reason }),
  sendProctorData: (sessionId, data) =>
    axios.post(`${API_URL}/exams/sessions/${sessionId}/proctor-data`, data),
  getTemplates: (params) => axios.get(`${API_URL}/exams/templates`, { params }),
  getStats: () => axios.get(`${API_URL}/exams/stats`),
  startExam: (examId) => axios.post(`${API_URL}/exams/${examId}/start`),
  createFromTemplate: (templateId, examData) =>
    axios.post(`${API_URL}/exams/create-from-template/${templateId}`, examData),
  getReport: (examId, format = "pdf") =>
    axios.get(`${API_URL}/exams/${examId}/report?format=${format}`, {
      responseType: "blob",
    }),
  getExamStatus: () => axios.get(`${API_URL}/exams/sessions/status`),
  resumeSession: (sessionId) =>
    axios.post(`${API_URL}/exams/sessions/${sessionId}/resume`),
  checkTimeEligibility: () => axios.get(`${API_URL}/exams/time-eligibility`),
};

export const examCandidateAPI = {
  getDashboard: (data) =>
    axios.post(`${API_URL}/exam-candidate/dashboard`, data),
  getExamDetails: (assignmentId) =>
    axios.get(`${API_URL}/exam-candidate/exam/${assignmentId}/details`),
  getProfile: () => axios.get(`${API_URL}/exam-candidate/profile`),
  getExams: () => axios.get(`${API_URL}/exam-candidate/exams`),
  getExamHistory: () => axios.get(`${API_URL}/exam-candidate/exam-history`),
  getExamResult: (resultId) =>
    axios.get(`${API_URL}/exam-candidate/result/${resultId}`),
  startSession: (assignmentId) =>
    axios.post(`${API_URL}/exam-candidate/exam/${assignmentId}/start`),
  getExamSession: (sessionId) =>
    axios.get(`${API_URL}/exam-candidate/session/${sessionId}`),
  saveAnswer: (sessionId, answerData) =>
    axios.post(`${API_URL}/exam-candidate/exam/answer`, {
      sessionId,
      ...answerData,
    }),
  updateProgress: (sessionId, progressData) =>
    axios.post(
      `${API_URL}/exam-candidate/session/${sessionId}/progress`,
      progressData
    ),
  sendProctoringData: (sessionId, proctoringData) =>
    axios.post(
      `${API_URL}/exam-candidate/session/${sessionId}/proctor`,
      proctoringData
    ),
  completeExam: (sessionId) =>
    axios.post(`${API_URL}/exam-candidate/exam/${sessionId}/complete`),
};

export const examQuestionsAPI = {
  getQuestions: (params) =>
    axios.get(`${API_URL}/exam-questions/questions`, { params }),
  getCategories: () => axios.get(`${API_URL}/exam-questions/categories`),
  autoSelect: (selections) =>
    axios.post(`${API_URL}/exam-questions/auto-select`, { selections }),
};

export const categoriesAPI = {
  getAll: () => axios.get(`${API_URL}/categories`),
  getFiltered: (questionBankName) => axios.post(`${API_URL}/categories/filtered`, { questionBankName: questionBankName }),
  getAllPaginated: (params) =>
    axios.post(`${API_URL}/categories/question_bank`, { params }),
  getAllCat: (params) => axios.post(`${API_URL}/categories/data_all`, params),
  getOne: (id) => axios.get(`${API_URL}/categories/${id}`),
  create: (categoryData) => axios.post(`${API_URL}/categories`, categoryData),
  update: (id, categoryData) =>
    axios.put(`${API_URL}/categories/${id}`, categoryData),
  delete: (id) => axios.delete(`${API_URL}/categories/${id}`),
};

export const questionTypesAPI = {
  getAll: () => axios.get(`${API_URL}/question-types`),
  getOne: (id) => axios.get(`${API_URL}/question-types/${id}`),
  create: (typeData) => axios.post(`${API_URL}/question-types`, typeData),
  update: (id, typeData) =>
    axios.put(`${API_URL}/question-types/${id}`, typeData),
  delete: (id) => axios.delete(`${API_URL}/question-types/${id}`),
};

export const settingsAPI = {
  getAll: () => axios.get(`${API_URL}/settings`),
  update: (settingsData) => axios.put(`${API_URL}/settings`, settingsData),
  getEmailSettings: () => axios.get(`${API_URL}/settings/email`),
  getSecuritySettings: () => axios.get(`${API_URL}/settings/security`),
  getExamSettings: () => axios.get(`${API_URL}/settings/exam`),
  updateEmailTemplates: (templates) =>
    axios.put(`${API_URL}/settings/email-templates`, templates),
  getSystemInfo: () => axios.get(`${API_URL}/settings/system-info`),
  getCloudStorageSettings: () => axios.get(`${API_URL}/settings/cloud-storage`),
  updateCloudStorageSettings: (cloudStorageData) =>
    axios.put(`${API_URL}/settings/cloud-storage`, cloudStorageData),
  testCloudStorageConnection: (cloudStorageData) =>
    axios.post(`${API_URL}/settings/test-cloud-storage`, cloudStorageData),
  testOpenAIConnection: (openaiData) =>
    axios.post(`${API_URL}/settings/test-openai`, openaiData),
};

export const analyticsAPI = {
  getDashboardStats: () => axios.get(`${API_URL}/analytics/dashboard`),
  getExamResults: (examId) => axios.get(`${API_URL}/analytics/exam/${examId}`),
  getCandidatePerformance: () => axios.get(`${API_URL}/analytics/candidates`),
  getExamAnalytics: (timeframe) =>
    axios.get(`${API_URL}/analytics/exams`, { params: { timeframe } }),
  getCategoryAnalytics: () => axios.get(`${API_URL}/analytics/categories`),
  getQuestionDifficulties: () => axios.get(`${API_URL}/analytics/questions`),
  exportAnalyticsReport: (filters, format = "csv") =>
    axios.post(
      `${API_URL}/analytics/export`,
      { filters, format },
      { responseType: "blob" }
    ),
  getDashboard: (params) =>
    axios.get(`${API_URL}/analytics/dashboard/data`, { params }),
  getCandidates: (params) =>
    axios.get(`${API_URL}/analytics/candidates`, { params }),
  getCandidateDetails: (id) =>
    axios.get(`${API_URL}/analytics/candidate/${id}`),
  getSessionDetails: (id) =>
    axios.get(`${API_URL}/analytics/exam-session/${id}`),
  exportReport: (id, format = "pdf") =>
    axios.get(`${API_URL}/analytics/export/${id}?format=${format}`, {
      responseType: "blob",
    }),
  downloadCandidateReport: (candidateId, examId) =>
    axios.get(
      `${API_URL}/analytics/candidate/${candidateId}/exam/${examId}/report`,
      {
        responseType: "blob",
      }
    ),
  getEnhancedDashboard: (params) =>
    axios.get(`${API_URL}/analytics/enhanced-dashboard`, { params }),

  getExamSessionDetail: async (sessionId) => {
    const response = await axios.get(`${API_URL}/analytics/exam-session/${sessionId}`);
    return response.data;
  },

  exportExamReport: async (sessionId) => {
    const response = await axios.get(`${API_URL}/analytics/export-exam/${sessionId}`, {
      responseType: "blob",
    });
    return response.data;
  },

  // Separate API calls for dashboard sections (for parallel loading)
  getUserStats: (params) =>
    axios.get(`${API_URL}/analytics/dashboard/user-stats`, { params }),
  getCompanyStats: (params) =>
    axios.get(`${API_URL}/analytics/dashboard/company-stats`, { params }),
  getSystemStats: (params) =>
    axios.get(`${API_URL}/analytics/dashboard/system-stats`, { params }),
};

// Super Admin Analytics API (separate endpoints for company_super_admin role)
export const superAdminAnalyticsAPI = {
  getEnhancedDashboard: (params) =>
    axios.get(`${API_URL}/super-admin-analytics/enhanced-dashboard`, { params }),
};

export const companyAPI = {
  getAll: () => axios.get(`${API_URL}/company`),
  getOne: (id) => axios.get(`${API_URL}/company/${id}`),
  update: (id, companyData) =>
    axios.put(`${API_URL}/company/${id}`, companyData),
  getAnalytics: (id) => axios.get(`${API_URL}/company/${id}/analytics`),
  updateSubscription: (id, subscriptionData) =>
    axios.put(`${API_URL}/company/${id}/subscription`, subscriptionData),
};

export const questionBankAPI = {
  getAll: (params) => axios.get(`${API_URL}/question-banks`, { params }),
  search: (params) => axios.get(`${API_URL}/question-banks/search`, { params }),
  getOne: (id) =>
    axios.get(`${API_URL}/question-banks/getQuestionBankById/${id}`),
  create: (bankData) => axios.post(`${API_URL}/question-banks`, bankData),
  update: (id, bankData) =>
    axios.put(`${API_URL}/question-banks/${id}`, bankData),
  delete: (id) =>
    axios.delete(`${API_URL}/question-banks/deleteQuestionBank/${id}`),
  deleteAllQuesion: (id) =>
    axios.delete(`${API_URL}/question-banks/deleteAllQuestionAndBank/${id}`),
  upload: (data) =>
    axios.post(`${API_URL}/question-banks/uploadQuestionBank`, data),
  submitValidated: (data) =>
    axios.post(`${API_URL}/question-banks/submitValidatedQuestions`, data),
  purchase: (bankData) =>
    axios.post(`${API_URL}/question-banks/purchase`, bankData),
  getPurchased: () => axios.get(`${API_URL}/question-banks/company/purchased`),
};

export const paymentAPI = {
  createStripeCheckout: (planData) =>
    axios.post(`${API_URL}/payment/create-stripe-checkout`, planData),
  createRazorpayOrder: (planData) =>
    axios.post(`${API_URL}/payment/create-razorpay-order`, planData),
  createPayPalOrder: (planData) =>
    axios.post(`${API_URL}/payment/create-paypal-order`, planData),
  verifyPayment: (paymentData) =>
    axios.post(`${API_URL}/payment/verify`, paymentData),
  getPaymentHistory: () => axios.get(`${API_URL}/payment/history`),
};

export const aiModuleAPI = {
  getCandidatesForSelection: (params) => 
    axios.get(`${API_URL}/ai-module/candidates`, { params }),
  extractResumeFromCandidates: (data) => 
    axios.post(`${API_URL}/ai-module/extract-resume`, data),
  generateQuestions: (data) => 
    axios.post(`${API_URL}/ai-module/generate-questions`, data),
  saveStep1: (data) => 
    axios.post(`${API_URL}/ai-module/step1`, data),
  saveStep2: (data) => 
    axios.post(`${API_URL}/ai-module/step2`, data),
  saveStep3: (data) => 
    axios.post(`${API_URL}/ai-module/step3`, data),
  saveStep4: (data) => 
    axios.post(`${API_URL}/ai-module/step4`, data),
  saveStep5: (data) => 
    axios.post(`${API_URL}/ai-module/step5`, data),
  getAIModuleById: (id) => 
    axios.get(`${API_URL}/ai-module/${id}`),
  getAIModules: (params) => 
    axios.get(`${API_URL}/ai-module`, { params }),
  updateCandidateResumeData: (aiModuleId, candidateId, data) => 
    axios.put(`${API_URL}/ai-module/${aiModuleId}/candidate/${candidateId}/resume-data`, data),
  uploadCandidateResume: (aiModuleId, candidateId, file) => {
    const formData = new FormData();
    formData.append('resume', file);
    return axios.post(`${API_URL}/ai-module/${aiModuleId}/candidate/${candidateId}/upload-resume`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  validateSkillInResume: (aiModuleId, candidateId, skill) => 
    axios.post(`${API_URL}/ai-module/${aiModuleId}/candidate/${candidateId}/validate-skill`, { skill }),
  testSkillValidation: (skill = "Java") => 
    axios.post(`${API_URL}/ai-module/test-skill-validation`, { skill }),
  getQuestionsGroupedByCandidateName: (aiModuleId) => 
    axios.get(`${API_URL}/ai-module/${aiModuleId}/questions`),
  createExamAssignments: (aiModuleId) => 
    axios.post(`${API_URL}/ai-module/create-exam-assignments`, { aiModuleId }),
  createBatchExamAssignments: (data) => 
    axios.post(`${API_URL}/ai-module/create-batch-exam-assignments`, data),
  recalculateSkillsMatching: (aiModuleId) => 
    axios.post(`${API_URL}/ai-module/${aiModuleId}/recalculate-skills-matching`),
  createCandidatesFromResumes: (files) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('resumes', file);
    });
    return axios.post(`${API_URL}/ai-module/create-candidates-from-resumes`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  // Manual Question Management
  addManualQuestions: (data) => 
    axios.post(`${API_URL}/ai-module/manual-questions`, data),
  getManualQuestionHistory: (params = {}) => 
    axios.get(`${API_URL}/ai-module/manual-questions/history`, { params }),
  assignHistoryQuestions: (data) => 
    axios.post(`${API_URL}/ai-module/manual-questions/assign-history`, data),
  updateManualQuestion: (data) =>
    axios.put(`${API_URL}/ai-module/manual-questions/update`, data), // Updates BOTH ai_manual and ai_modules collections
  deleteManualQuestion: (data) =>
    axios.delete(`${API_URL}/ai-module/manual-questions/delete`, { data }), // Removes ONLY from ai_modules (preserves ai_manual for reuse)
};

export const jobDescriptionAPI = {
  getAll: (params) => axios.get(`${API_URL}/job-descriptions`, { params }),
  getOne: (id) => axios.get(`${API_URL}/job-descriptions/${id}`),
  create: (jobData) => axios.post(`${API_URL}/job-descriptions`, jobData),
  update: (id, jobData) => axios.put(`${API_URL}/job-descriptions/${id}`, jobData),
  delete: (id) => axios.delete(`${API_URL}/job-descriptions/${id}`),
  getStats: () => axios.get(`${API_URL}/job-descriptions/stats`),
};

export const departmentAPI = {
  getAll: (params) => axios.get(`${API_URL}/departments`, { params }),
  getOne: (id) => axios.get(`${API_URL}/departments/${id}`),
  create: (data) => axios.post(`${API_URL}/departments`, data),
  update: (id, data) => axios.put(`${API_URL}/departments/${id}`, data),
  archive: (id) => axios.patch(`${API_URL}/departments/${id}/archive`),
  restore: (id) => axios.patch(`${API_URL}/departments/${id}/restore`),
  addMember: (id, userId) => axios.post(`${API_URL}/departments/${id}/member`, { userId }),
  removeMember: (id, memberId) => axios.delete(`${API_URL}/departments/${id}/member/${memberId}`),
  getActivity: (id, params) => axios.get(`${API_URL}/departments/${id}/activity`, { params }),
  getCompanyUsers: () => axios.get(`${API_URL}/departments/company-users`),
};

export const candidateV2API = {
  getAll: async (params) => {
    const res = await axios.get(`${API_URL}/candidates-v2`, { params });
    return res.data;
  },
  getOne: async (id) => {
    const res = await axios.get(`${API_URL}/candidates-v2/${id}`);
    return res.data;
  },
  create: async (data) => {
    const res = await axios.post(`${API_URL}/candidates-v2`, data);
    return res.data;
  },
  update: async (id, data) => {
    const res = await axios.put(`${API_URL}/candidates-v2/${id}`, data);
    return res.data;
  },
  archive: async (id) => {
    const res = await axios.patch(`${API_URL}/candidates-v2/${id}/archive`);
    return res.data;
  },
  restore: async (id) => {
    const res = await axios.patch(`${API_URL}/candidates-v2/${id}/restore`);
    return res.data;
  },
  moveStage: async (id, stage) => {
    const res = await axios.patch(`${API_URL}/candidates-v2/${id}/stage`, { stage });
    return res.data;
  },
  getStageHistory: async (id, params) => {
    const res = await axios.get(`${API_URL}/candidates-v2/${id}/stage-history`, { params });
    return res.data;
  },
  getActivities: async (id, params) => {
    const res = await axios.get(`${API_URL}/candidates-v2/${id}/activities`, { params });
    return res.data;
  },
  addActivity: async (id, data) => {
    const res = await axios.post(`${API_URL}/candidates-v2/${id}/activities`, data);
    return res.data;
  },
  getNotes: async (id, params) => {
    const res = await axios.get(`${API_URL}/candidates-v2/${id}/notes`, { params });
    return res.data;
  },
  addNote: async (id, data) => {
    const res = await axios.post(`${API_URL}/candidates-v2/${id}/notes`, data);
    return res.data;
  },
  getCompanyUsers: async () => {
    const res = await axios.get(`${API_URL}/candidates-v2/company-users`);
    return res.data;
  },
};
